import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import Select from 'react-select'
import useSWR from 'swr'
import { api, createPlayer, updatePlayer } from '../services/api'
import { Badge, Banner, Button, Panel, ImageManagement, SlugManager } from '../components'
import OverridableField from '../components/OverridableField'
import pageStyles from '../styles/page.module.scss'
import formStyles from '../styles/forms.module.scss'
import buttonStyles from '../components/Button.module.scss'
import selectStyles from '../styles/react-select.module.scss'

interface Player {
  tc_competitor_id: string
  ext_competitor_id: string | null
  tc_name: string | null
  ext_name: string | null
  tc_short_name: string | null
  ext_short_name: string | null
  tc_abbreviation: string | null
  ext_abbreviation: string | null
  tc_team_type: string | null
  ext_team_type: string | null
  tc_country_code: string | null
  ext_country_code: string | null
  country_name: string | null
  country_flag_url: string | null
  sport_name: string | null
  catalog_source_name: string | null
  effective_virtual: boolean
  created_at: string | null
  tc_updated_at: string | null
  ext_updated_at: string | null
}

interface PlayerDetailResponse {
  player: Player
  timestamp: string
}

interface FormState {
  tc_name: string
  tc_short_name: string
  tc_abbreviation: string
  tc_country_code: string
}

interface CountryOption {
  value: string
  label: string
}

const PlayerDetail: React.FC = () => {
  const { playerId } = useParams<{ playerId: string }>()
  const navigate = useNavigate()
  const isCreateMode = playerId === 'new'

  const { data, isLoading, error, mutate } = useSWR(
    playerId && !isCreateMode ? ['player-detail', playerId] : null,
    () => api.get(`/players/${playerId}`),
    { revalidateOnFocus: false }
  )

  const { data: countriesData } = useSWR<{ countries: CountryOption[] }>(
    'countries',
    () => api.get('/publishing/countries'),
    { revalidateOnFocus: false }
  )
  const countryOptions: CountryOption[] = countriesData?.countries || []

  const response: PlayerDetailResponse | null = data?.body ? JSON.parse(data.body) : data
  const player = response?.player

  const [form, setForm] = useState<FormState>({
    tc_name: '',
    tc_short_name: '',
    tc_abbreviation: '',
    tc_country_code: '',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const getEffective = (tc: string | null, ext: string | null) => tc || ext || ''

  const resetForm = useCallback((p: Player) => {
    setForm({
      tc_name: getEffective(p.tc_name, p.ext_name),
      tc_short_name: getEffective(p.tc_short_name, p.ext_short_name),
      tc_abbreviation: getEffective(p.tc_abbreviation, p.ext_abbreviation),
      tc_country_code: getEffective(p.tc_country_code, p.ext_country_code),
    })
  }, [])

  useEffect(() => {
    if (player) resetForm(player)
  }, [player, resetForm])

  const hasChanges = (): boolean => {
    if (!player) return false
    return (
      form.tc_name !== getEffective(player.tc_name, player.ext_name) ||
      form.tc_short_name !== getEffective(player.tc_short_name, player.ext_short_name) ||
      form.tc_abbreviation !== getEffective(player.tc_abbreviation, player.ext_abbreviation) ||
      form.tc_country_code !== getEffective(player.tc_country_code, player.ext_country_code)
    )
  }

  const handleCreate = async () => {
    if (!form.tc_name.trim()) {
      setSaveError('Player name is required')
      return
    }

    setIsSaving(true)
    setSaveError(null)

    try {
      const result = await createPlayer({
        tc_name: form.tc_name.trim(),
        tc_short_name: form.tc_short_name.trim() || null,
        tc_abbreviation: form.tc_abbreviation.trim() || null,
        tc_country_code: form.tc_country_code.trim() || null,
      })
      const newId = result.player?.tc_competitor_id
      navigate(`/players/${newId}`, { replace: true })
    } catch (err: any) {
      setSaveError(err.message || 'Failed to create player')
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = async () => {
    if (!playerId || !player) return

    setIsSaving(true)
    setSaveError(null)
    setSaveSuccess(null)

    try {
      await updatePlayer(playerId, {
        tc_name: form.tc_name === (player.ext_name || '') ? null : form.tc_name || null,
        tc_short_name: form.tc_short_name === (player.ext_short_name || '') ? null : form.tc_short_name || null,
        tc_abbreviation: form.tc_abbreviation === (player.ext_abbreviation || '') ? null : form.tc_abbreviation || null,
        tc_country_code: form.tc_country_code === (player.ext_country_code || '') ? null : form.tc_country_code || null,
      })
      await mutate()
      setSaveSuccess('Player updated successfully')
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update player')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (player) resetForm(player)
    setSaveError(null)
    setSaveSuccess(null)
  }

  const handleRevert = (field: keyof FormState, extValue: string | null) => {
    setForm(prev => ({ ...prev, [field]: extValue || '' }))
  }

  const getPlayerName = (): string => {
    if (!player) return 'Unknown Player'
    return player.tc_name || player.ext_name || 'Unknown Player'
  }

  const getMatchesUrl = () => {
    const startDate = new Date()
    startDate.setFullYear(startDate.getFullYear() - 1)
    const endDate = new Date()
    endDate.setFullYear(endDate.getFullYear() + 1)
    return `/matches?playerIds=${playerId}&startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]}`
  }

  // Create mode
  if (isCreateMode) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.pageHeader}>
          <h1>Create Player</h1>
          <p>Create a new player record. You can add images after creating the player.</p>
        </div>

        {saveError && (
          <Banner
            variant="error"
            message={saveError}
            onDismiss={() => setSaveError(null)}
          />
        )}

        <div className={formStyles.formContainer}>
          <div className={formStyles.formField}>
            <div className={formStyles.fieldGroup}>
              <label>Full Name *</label>
              <input
                type="text"
                value={form.tc_name}
                onChange={e => setForm(prev => ({ ...prev, tc_name: e.target.value }))}
                placeholder="Full name"
                disabled={isSaving}
                autoFocus
              />
            </div>

            <div className={formStyles.fieldGroup}>
              <label>Short Name</label>
              <input
                type="text"
                value={form.tc_short_name}
                onChange={e => setForm(prev => ({ ...prev, tc_short_name: e.target.value }))}
                placeholder="Short name"
                disabled={isSaving}
              />
            </div>

            <div className={formStyles.fieldGroup}>
              <label>Abbreviation</label>
              <input
                type="text"
                value={form.tc_abbreviation}
                onChange={e => setForm(prev => ({ ...prev, tc_abbreviation: e.target.value }))}
                placeholder="Abbreviation"
                disabled={isSaving}
              />
            </div>

            <div className={formStyles.fieldGroup}>
              <label>Country</label>
              <Select
                options={countryOptions}
                value={countryOptions.find(o => o.value === form.tc_country_code) || null}
                onChange={option => setForm(prev => ({ ...prev, tc_country_code: option?.value || '' }))}
                isClearable
                isDisabled={isSaving}
                placeholder="Select country..."
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            </div>

          </div>

          <div className={formStyles.formFooter}>
            <Link
              to="/players"
              className={`${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.medium}`}
            >
              Cancel
            </Link>
            <Button
              onClick={handleCreate}
              disabled={!form.tc_name.trim() || isSaving}
            >
              {isSaving ? 'Creating...' : 'Create Player'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading player</h2>
          <p>Failed to fetch player details: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  // Loading state
  if (isLoading || !player) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading player details...</h3>
          <p>Please wait while we fetch the player data.</p>
        </div>
      </div>
    )
  }

  // Edit mode
  return (
    <div>
      {/* Header Button */}
      <div className={pageStyles.headerButtons}>
        <Link
          to={getMatchesUrl()}
          className={`${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.medium}`}
        >
          View Matches
        </Link>
      </div>

      {/* Header */}
      <div className={pageStyles.pageHeader}>
        <h1>{getPlayerName()}</h1>
        <p>
          {player.ext_competitor_id && (
            <code className="id-code">{player.ext_competitor_id}</code>
          )}
          {' '}
          {player.sport_name && <Badge variant="primary">{player.sport_name}</Badge>}
          {' '}
          {player.catalog_source_name && <Badge variant="secondary">{player.catalog_source_name}</Badge>}
          {' '}
          {player.effective_virtual && <Badge variant="warning">Placeholder</Badge>}
          {' '}
          {player.country_name && <Badge variant="neutral">{player.country_name}</Badge>}
        </p>
      </div>

      {saveSuccess && (
        <Banner
          variant="success"
          message={saveSuccess}
          onDismiss={() => setSaveSuccess(null)}
        />
      )}
      {saveError && (
        <Banner
          variant="error"
          message={saveError}
          onDismiss={() => setSaveError(null)}
        />
      )}

      <Panel>
        <Panel.Header>
          <h2>Player Information</h2>
        </Panel.Header>

        <div className={formStyles.formField}>
          <OverridableField
            label="Full Name"
            tcValue={form.tc_name || null}
            extValue={player.ext_name}
            onRevert={() => handleRevert('tc_name', player.ext_name)}
            modifiedClassName={formStyles.modified}
          >
            <input
              type="text"
              value={form.tc_name}
              onChange={e => setForm(prev => ({ ...prev, tc_name: e.target.value }))}
              placeholder="Full name"
              disabled={isSaving}
            />
          </OverridableField>

          <OverridableField
            label="Short Name"
            tcValue={form.tc_short_name || null}
            extValue={player.ext_short_name}
            onRevert={() => handleRevert('tc_short_name', player.ext_short_name)}
            modifiedClassName={formStyles.modified}
          >
            <input
              type="text"
              value={form.tc_short_name}
              onChange={e => setForm(prev => ({ ...prev, tc_short_name: e.target.value }))}
              placeholder="Short name"
              disabled={isSaving}
            />
          </OverridableField>

          <OverridableField
            label="Abbreviation"
            tcValue={form.tc_abbreviation || null}
            extValue={player.ext_abbreviation}
            onRevert={() => handleRevert('tc_abbreviation', player.ext_abbreviation)}
            modifiedClassName={formStyles.modified}
          >
            <input
              type="text"
              value={form.tc_abbreviation}
              onChange={e => setForm(prev => ({ ...prev, tc_abbreviation: e.target.value }))}
              placeholder="Abbreviation"
              disabled={isSaving}
            />
          </OverridableField>

          <OverridableField
            label="Country"
            tcValue={form.tc_country_code || null}
            extValue={player.ext_country_code}
            extDisplayValue={player.country_name || player.ext_country_code}
            onRevert={() => handleRevert('tc_country_code', player.ext_country_code)}
          >
            <Select
              options={countryOptions}
              value={countryOptions.find(o => o.value === form.tc_country_code) || null}
              onChange={option => setForm(prev => ({ ...prev, tc_country_code: option?.value || '' }))}
              isClearable
              isDisabled={isSaving}
              placeholder="Select country..."
              className={selectStyles.reactSelect}
              classNamePrefix="react-select"
            />
          </OverridableField>

          {(player.country_name || getEffective(player.tc_country_code, player.ext_country_code)) && (
            <div className={formStyles.fieldGroup}>
              <label>Country flag</label>
              <div className={formStyles.readOnlyImage}>
                {player.country_flag_url ? (
                  <img
                    src={player.country_flag_url}
                    alt={`Flag of ${player.country_name || 'country'}`}
                    width={48}
                    height={32}
                  />
                ) : (
                  <span className={formStyles.hint}>Missing</span>
                )}
              </div>
            </div>
          )}
        </div>

        {hasChanges() && (
          <div className={formStyles.formFooter}>
            <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
      </Panel>

      <SlugManager entityType="player" entityId={player.tc_competitor_id} />

      <Panel>
        <Panel.Header>
          <h2>Images</h2>
        </Panel.Header>
        <ImageManagement entityType="player" entityId={player.tc_competitor_id} />
      </Panel>
    </div>
  )
}

export default PlayerDetail
